import api from '../utils/api';
import type { User, Role } from '../types/user.types';
import type { PaginatedResponse, QueryParams } from '../types/common.types';

export interface CreateUserDto {
  email: string;
  password: string;
  nome: string;
  cognome: string;
  ruolo: Role;
}

export interface UpdateUserDto {
  email?: string;
  nome?: string;
  cognome?: string;
  ruolo?: Role;
  isActive?: boolean;
}

export interface AssignTipologieDto {
  tipologieIds: string[];
}

export interface UpdatePasswordDto {
  currentPassword: string;
  newPassword: string;
}

export interface ResetPasswordDto {
  userId: string;
  newPassword: string;
}

export interface UserFilters {
  ruolo?: Role;
  isActive?: boolean;
  search?: string;
  includeDeleted?: boolean;
}

export const usersService = {
  // Get all users with pagination and filters
  getAll: async (params: QueryParams & UserFilters): Promise<PaginatedResponse<User>> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  // Get user by ID
  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  // Create new user
  create: async (data: CreateUserDto): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },

  // Update user
  update: async (id: string, data: UpdateUserDto): Promise<User> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  // Delete user
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  // Update own password
  updatePassword: async (data: UpdatePasswordDto): Promise<void> => {
    await api.post('/users/password/update', data);
  },

  // Reset user password (admin only)
  resetPassword: async (data: ResetPasswordDto): Promise<void> => {
    await api.post('/users/password/reset', data);
  },

  // Toggle user active status
  toggleActive: async (id: string): Promise<User> => {
    const response = await api.patch(`/users/${id}/toggle-active`);
    return response.data;
  },

  // Get operatori (users with OPERATORE role)
  getOperatori: async (): Promise<User[]> => {
    const response = await api.get('/users/operatori');
    return response.data;
  },

  // Assign tipologie to user
  assignTipologie: async (id: string, data: AssignTipologieDto): Promise<User> => {
    const response = await api.patch(`/users/${id}/assign-tipologie`, data);
    return response.data;
  },

  // Restore soft-deleted user
  restore: async (id: string): Promise<User> => {
    const response = await api.patch(`/users/${id}/restore`);
    return response.data;
  },

  // Hard delete user (permanent)
  hardDelete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}/permanent`);
  }
};

export default usersService;
